import { useEffect, useMemo, useReducer, useRef, type ReactNode } from 'react';
import {
  envRequestContext,
  setAuthTokenProvider,
  setRequestContextProvider,
  setStaleAcademicYearHandler,
} from '@/lib/apiClient';
import { setChosenSchool } from '@/api/schoolScope';
import { hasSignedOut } from '@/auth/sessionLifecycle';
import { renewB2CAccessToken } from '@/auth/b2c';
import { configureFormatting } from '@/lib/format';
import { CamptainLoaderOverlay } from '@/components/feedback/CamptainLoader';
import { GlobalContext, type GlobalContextValue } from './globalContext';
import { GlobalAction, globalReducer, loadInitialState, persistState } from './globalState';

/**
 * Holds the app-wide client state (see `globalState.ts`). State is restored
 * from storage on start and written back whenever it changes.
 */
export function GlobalStateProvider({ children }: { children: ReactNode }) {
  const [state, dispatch] = useReducer(globalReducer, undefined, () => loadInitialState());

  // Applied during render (idempotent) so children rendering in this same pass
  // already format with the new settings.
  configureFormatting(state.appSettings);

  // The school chosen in the header, for the routes that act for it without
  // naming it (api/schoolScope.ts). Set during render, like the formatting above,
  // so a child's first request already sees it.
  setChosenSchool(state.activeSchoolId);

  useEffect(() => {
    persistState(state);
  }, [state]);

  // The API client reads the token on every request; a ref keeps that read current
  // without re-registering the provider on each state change. The public admission
  // portal's OTP session is a separate identity from office sign-in, but shares the
  // same bearer-token seam — only one of the two is ever set in a given browser tab.
  const portalTokenRef = useRef(state.applicantSession?.token);
  const tokenRef = useRef(state.token ?? state.applicantSession?.token);
  useEffect(() => {
    tokenRef.current = state.token ?? state.applicantSession?.token;
    portalTokenRef.current = state.applicantSession?.token;
  }, [state.token, state.applicantSession?.token]);
  // After B2C sign-in the school and branch come from the users table (global state); the
  // API checks the person belongs to that school. Set during render, like the year below.
  const accessRef = useRef(state.access);
  // eslint-disable-next-line react-hooks/refs -- render-time sync, see above
  accessRef.current = state.access;
  useEffect(() => {
    // Signed in with B2C: a current token from MSAL's cache (renewed when it nears expiry).
    setAuthTokenProvider(async () => {
      if (hasSignedOut()) return portalTokenRef.current;
      const token = accessRef.current
        ? ((await renewB2CAccessToken()) ?? tokenRef.current)
        : tokenRef.current;
      return hasSignedOut() ? portalTokenRef.current : token;
    });
  }, []);

  // Organization and branch come from the environment until sign-in supplies
  // them; the acting user is the signed-in user when there is one.
  const userIdRef = useRef(state.user?.userId);
  useEffect(() => {
    userIdRef.current = state.user?.userId;
  }, [state.user?.userId]);
  // The working academic year (X-Academic-Year-Id). None sends no header, and the API
  // falls back to the branch's current year. Set during render, like the chosen school
  // above, so a request right after a school switch never pairs the new school with the
  // old school's year.
  const activeRoleRef = useRef(state.activeRole);
  // eslint-disable-next-line react-hooks/refs -- render-time sync, like the school and year
  activeRoleRef.current = state.activeRole;
  const academicYearIdRef = useRef(state.activeAcademicYearId);
  // eslint-disable-next-line react-hooks/refs -- render-time sync, see above
  academicYearIdRef.current = state.activeAcademicYearId;
  useEffect(() => {
    setRequestContextProvider(() => {
      const env = envRequestContext();
      const access = accessRef.current;
      return {
        ...env,
        ...(access
          ? {
              organizationId: access.activeOrganization.organizationId,
              ...(access.branchId ? { branchId: access.branchId } : {}),
            }
          : {}),
        userId: userIdRef.current ?? env.userId,
        academicYearId: academicYearIdRef.current,
        // Signed in: every call acts as the chosen role only (X-Camptain-Active-Role).
        activeRoleId: access?.roles.find((r) => r.code === activeRoleRef.current)?.roleId ?? null,
      };
    });
  }, []);

  // A stale working year (deleted, or from another school) fails every request with a 400.
  // Drop it so useCurrentAcademicYear falls back to the school's current year. Only once
  // per stale year: the ref is cleared straight away, so the other requests that fail
  // with the same year (and any later render) do not dispatch again.
  useEffect(() => {
    setStaleAcademicYearHandler((yearId) => {
      if (academicYearIdRef.current !== yearId) return;
      academicYearIdRef.current = null;
      dispatch({ type: GlobalAction.SetActiveAcademicYear, yearId: null });
    });
    return () => setStaleAcademicYearHandler(undefined);
  }, []);

  // `dispatch` is stable, so the value only changes with state — consumers
  // don't re-render on unrelated provider renders.
  const value = useMemo<GlobalContextValue>(() => ({ state, dispatch }), [state]);

  return (
    <GlobalContext value={value}>
      {children}
      {/* The app-wide loading overlay, shown while anything sets `busy`. */}
      <CamptainLoaderOverlay
        open={state.busy}
        {...(state.busyLabel ? { label: state.busyLabel } : {})}
      />
    </GlobalContext>
  );
}
